import dayjs from 'dayjs'
import {
	SettlementCurrency,
	SettlementRowType,
	SettlementMonthSummaryType,
} from '@schemas'

/**
 * 정산 한 페이지 — 연간 · 월간 · 주간 탭 (2026-09-27 사용자 결정). 모든 금액은 원화 환산.
 * 환율은 화면에서 바꾸지 않는다 — 주문이 만들어질 때 서버가 박은 `usdKrwRate`(ORDER.USD_KRW_RATE)가 행마다 온다.
 */
export const SETTLEMENT_PATH = '/main/settlement'

export type SettlementUnit = 'year' | 'month' | 'week'

/** 주문 통화 값 → 원화. KRW 는 그대로, USD 는 × 그 주문의 환율(원 단위 반올림) */
export const toKrw = (
	currency: SettlementCurrency,
	value: number | null | undefined,
	rate: number,
): number | null => {
	if (value === null || value === undefined) return null
	return currency === 'KRW' ? Math.round(value) : Math.round(value * rate)
}

/** 원화 표기 */
export const krw = (value: number | null | undefined): string =>
	value === null || value === undefined
		? ''
		: `₩${Math.round(value).toLocaleString('ko-KR')}`

/** 원래 통화 표기 — 툴팁용 */
export const originalMoney = (
	currency: SettlementCurrency,
	value: number | null | undefined,
): string => {
	if (value === null || value === undefined) return ''
	return currency === 'KRW'
		? `₩${Math.round(value).toLocaleString('ko-KR')}`
		: `$${value.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`
}

export const SETTLEMENT_STATUS_TITLE: { [key: string]: string } = {
	sold: '판매',
	refunded: '환불',
	canceled: '취소',
	awaitingPayment: '입금 대기',
}
export const SETTLEMENT_STATUS_CHIP: { [key: string]: string } = {
	awaitingPayment: 'awaitingPayment',
	refunded: 'refunded',
	canceled: 'deleted',
}
export const SETTLEMENT_CHANNEL_TITLE: { [key: string]: string } = {
	retail: '소매',
	wholesale: '도매',
}

// ── 주 ──

/** 아무 날 → 그 주 월요일 'YYYY-MM-DD' (ISO 주, 월~일) */
export const weekStartOf = (date: string | Date): string => {
	const d = dayjs(date)
	const offset = (d.day() + 6) % 7
	return d.subtract(offset, 'day').format('YYYY-MM-DD')
}
export const weekEndOf = (weekStart: string): string =>
	dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD')
/** 주가 걸친 달('YYYY-MM') — 하나 또는 둘 */
export const weekMonths = (weekStart: string): string[] => {
	const a = dayjs(weekStart).format('YYYY-MM')
	const b = dayjs(weekEndOf(weekStart)).format('YYYY-MM')
	return a === b ? [a] : [a, b]
}
export const weekTitle = (weekStart: string): string => {
	const s = dayjs(weekStart)
	const e = dayjs(weekEndOf(weekStart))
	return `${s.format('YYYY년 M월 D일')} – ${
		s.year() === e.year() && s.month() === e.month()
			? e.format('D일')
			: e.format('M월 D일')
	}`
}
export const rowInWeek = (row: SettlementRowType, weekStart: string): boolean => {
	const key = dayjs(row.orderedAt).format('YYYY-MM-DD')
	return key >= weekStart && key <= weekEndOf(weekStart)
}

// ── 집계(원화) ──

/** 기간 하나의 집계 — 서버 월 집계와 같은 열, 전부 원화 */
export interface SettlementAggregate {
	orderCount: number
	eligibleCount: number
	awaitingCount: number
	awaitingSale: number
	saleTotal: number
	vatTotal: number
	vatPaid: number
	vatUnpaid: number
	donationTotal: number
	donationPaid: number
	donationUnpaid: number
	taxInvoiceCount: number
	costMissingCount: number
	/** USD 주문이 하나라도 있으면 true(「주문 시점 환율로 환산」 안내) */
	hasUsd: boolean
}

export const emptyAggregate = (): SettlementAggregate => ({
	orderCount: 0,
	eligibleCount: 0,
	awaitingCount: 0,
	awaitingSale: 0,
	saleTotal: 0,
	vatTotal: 0,
	vatPaid: 0,
	vatUnpaid: 0,
	donationTotal: 0,
	donationPaid: 0,
	donationUnpaid: 0,
	taxInvoiceCount: 0,
	costMissingCount: 0,
	hasUsd: false,
})

/** 판매 행 → 집계(원화). 월간·주간 탭의 큰 수. 행마다 자기 주문 시점 환율 */
export const aggregateRows = (rows: SettlementRowType[]): SettlementAggregate => {
	const a = emptyAggregate()
	rows.forEach(r => {
		const k = (v: number | null | undefined) => toKrw(r.currency, v, r.usdKrwRate) ?? 0
		a.orderCount += 1
		if (r.currency === 'USD') a.hasUsd = true
		if (r.status === 'awaitingPayment') {
			a.awaitingCount += 1
			a.awaitingSale += k(r.paidAmount)
			return
		}
		if (r.taxInvoiceYn === 1) a.taxInvoiceCount += 1
		if (r.costMissingQty > 0) a.costMissingCount += 1
		if (!r.hasSettlement || r.eligibleYn !== 1) return
		a.eligibleCount += 1
		a.saleTotal += k(r.saleAmount)
		const vat = k(r.vatReserve)
		const don = k(r.donationReserve)
		a.vatTotal += vat
		a.donationTotal += don
		if (r.vatPaidYn === 1) a.vatPaid += vat
		else a.vatUnpaid += vat
		if (r.donationPaidYn === 1) a.donationPaid += don
		else a.donationUnpaid += don
	})
	return a
}

export interface SettlementMonthKrw extends SettlementAggregate {
	/** Grid 행 키 = 월('YYYY-MM'). 합계 행은 'total' */
	id: string
	month: string
	isTotal: boolean
}

export const sumAggregates = (list: SettlementAggregate[]): SettlementAggregate =>
	list.reduce((acc, a) => {
		acc.orderCount += a.orderCount
		acc.eligibleCount += a.eligibleCount
		acc.awaitingCount += a.awaitingCount
		acc.awaitingSale += a.awaitingSale
		acc.saleTotal += a.saleTotal
		acc.vatTotal += a.vatTotal
		acc.vatPaid += a.vatPaid
		acc.vatUnpaid += a.vatUnpaid
		acc.donationTotal += a.donationTotal
		acc.donationPaid += a.donationPaid
		acc.donationUnpaid += a.donationUnpaid
		acc.taxInvoiceCount += a.taxInvoiceCount
		acc.costMissingCount += a.costMissingCount
		acc.hasUsd = acc.hasUsd || a.hasUsd
		return acc
	}, emptyAggregate())

/** 서버의 월 × 통화 집계 → 월 하나로 합친다. 원화 합은 서버가 주문 시점 환율로 계산한 `*Krw` 열 */
export const mergeSummaryKrw = (
	summary: SettlementMonthSummaryType[],
): SettlementMonthKrw[] => {
	const byMonth = new Map<string, SettlementMonthKrw>()
	summary.forEach(s => {
		const m = byMonth.get(s.month) ?? {
			id: s.month,
			month: s.month,
			isTotal: false,
			...emptyAggregate(),
		}
		m.orderCount += s.orderCount
		m.eligibleCount += s.eligibleCount
		m.awaitingCount += s.awaitingCount
		m.awaitingSale += s.awaitingSaleKrw
		m.saleTotal += s.saleTotalKrw
		m.vatTotal += s.vatTotalKrw
		m.vatPaid += s.vatPaidKrw
		m.vatUnpaid += s.vatUnpaidKrw
		m.donationTotal += s.donationTotalKrw
		m.donationPaid += s.donationPaidKrw
		m.donationUnpaid += s.donationUnpaidKrw
		m.taxInvoiceCount += s.taxInvoiceCount
		m.costMissingCount += s.costMissingCount
		if (s.currency === 'USD') m.hasUsd = true
		byMonth.set(s.month, m)
	})
	return Array.from(byMonth.values()).sort((x, y) => (x.month < y.month ? -1 : 1))
}

/** 연간 표의 마지막 행 — 합계 */
export const totalRowKrw = (months: SettlementMonthKrw[]): SettlementMonthKrw => ({
	id: 'total',
	month: '',
	isTotal: true,
	...sumAggregates(months),
})
